import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { IoMdArrowBack } from "react-icons/io";
import { FiArrowUpRight, FiPlus, FiBriefcase, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { withdrawal, getUserAccounts } from "../../api/index";

export default function WithdrawPage() {
  const token = localStorage.getItem("token");
  const decoded = jwtDecode(token);
  const userId = decoded.id || decoded._id;

  const navigate = useNavigate();
  const location = useLocation();
  const balance = location.state?.amount || 0;

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await getUserAccounts(userId);
        setAccounts(res.data);
        if (res.data?.length > 0) setSelectedAccount(res.data[0]?._id);
      } catch (err) {
        setMessage("Failed to load accounts. Please try again.");
      }
    };
    fetchAccounts();
  }, [userId]);

  const handleWithdraw = async () => {
    if (!withdrawAmount || withdrawAmount <= 0) {
      setMessage("Enter a valid amount");
      return;
    }
    if (!selectedAccount) {
      setMessage("Select an account");
      return;
    }
    if (parseFloat(withdrawAmount) > balance) {
      setMessage("❌ Withdrawal exceeds available balance");
      return;
    }

    const account = accounts.find((a) => a._id === selectedAccount);

    setLoading(true);
    setMessage("");
    try {
      const res = await withdrawal({
        userId,
        amount: withdrawAmount,
        bankName: account.bankName,
        accountNumber: account.accountNumber,
        accountHolder: account.accountHolder,
      });
      setMessage("Withdraw is being processed");
      setWithdrawAmount("");
      setTimeout(() => navigate(-1), 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || "Withdrawal failed";
      setMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const isError = message.toLowerCase().includes("fail") || 
                  message.toLowerCase().includes("error") || 
                  message.toLowerCase().includes("exceed");

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight pr-10">Withdraw</h1>
        <div />
      </div>

      <div className="max-w-md mx-auto p-6 pt-10">
        {/* 💰 Balance Overview Card */}
        <div className="bg-gray-900 p-8 rounded-[2.5rem] shadow-2xl shadow-gray-400 mb-10 text-center relative overflow-hidden">
          <div className="absolute top-[-20%] left-[-10%] w-32 h-32 bg-green-500/20 rounded-full blur-3xl"></div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 relative z-10">Available for Payout</p>
          <h2 className="text-3xl font-black text-white relative z-10">₦{balance.toLocaleString()}</h2>
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-8">
          {/* Amount Input */}
         {/* Amount Input */}
<div className="space-y-3">
  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Withdrawal Amount</label>
  <div className="relative">
    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-xl font-black text-gray-400">₦</span>
    <input
      type="text" // Changed to text to better control filtering
      inputMode="decimal" // Opens the number pad on mobile
      value={withdrawAmount}
      onChange={(e) => {
        const val = e.target.value;
        // This regex only allows numbers and a single decimal point
        if (val === "" || /^\d*\.?\d*$/.test(val)) {
          setWithdrawAmount(val);
        }
      }}
      className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-[1.5rem] py-5 px-12 text-2xl font-black text-gray-900 transition-all outline-none"
      placeholder="0.00"
    />
  </div>
</div>

          {/* Account Selection */}
          <div className="space-y-3">
            <div className="flex justify-between items-center px-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Destination Account</label>
              <button 
                onClick={() => navigate("/addbankdetails")}
                className="text-[10px] font-black text-orange-600 uppercase tracking-tighter flex items-center gap-1"
              >
                <FiPlus /> Add New
              </button>
            </div>
            
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <FiBriefcase size={18} />
              </div>
              <select
                value={selectedAccount || ""}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-[1.2rem] py-4 pl-12 pr-4 text-sm font-bold text-gray-900 appearance-none outline-none transition-all cursor-pointer"
              >
                {accounts.length === 0 ? (
                  <option disabled>No accounts found</option>
                ) : (
                  accounts.map((acc) => (
                    <option key={acc._id} value={acc._id}>
                      {acc.bankName} • {acc.accountNumber}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Status Message */}
          {message && (
            <div className={`flex items-center gap-3 p-4 rounded-2xl border ${
              isError ? "bg-red-50 text-red-600 border-red-100" : "bg-green-50 text-green-600 border-green-100"
            }`}>
              {isError ? <FiAlertCircle className="shrink-0" /> : <FiCheckCircle className="shrink-0" />}
              <p className="text-[11px] font-black uppercase tracking-tight leading-tight">{message}</p>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={handleWithdraw}
            disabled={loading}
            className={`w-full py-5 rounded-[1.5rem] font-black uppercase tracking-widest text-sm transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 ${
              loading 
                ? "bg-gray-200 text-gray-400 cursor-not-allowed" 
                : "bg-orange-600 text-white hover:bg-orange-700 shadow-orange-200"
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
            ) : (
              <>
                <FiArrowUpRight size={18} strokeWidth={3} />
                Confirm Withdrawal
              </>
            )}
          </button>
        </div>

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Payout • Verified Bank Details • OurASAP
        </p>
      </div>
    </div>
  );
}